import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import MessageDialog from 'views/Components/CommonCustomComponents/messageDialog/MessageNotification';
import { checkPermissionGet } from 'utils/API/Merchant APIs/MerchantApiCalls';
import { globalNotifySuccess } from 'views/utilities/AlertToast';
import { identifyServiceByRoute } from './serviceMapping.config';

/**
 * Generic Route Guard Component
 *
 * This component restricts access to routes based on configurable conditions.
 * It checks localStorage flags and redirects users to specific routes when conditions are met.
 * It also checks service-level restrictions from Redux state.
 */
const RouteGuard = ({ children, restrictions = [] }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const rekycRequired = useSelector((state) => state.auth?.rekycRequired);
    const blockedServices = useSelector((state) => state.checkMerchantOrBc.blockedServices);
    const [dialogConfig, setDialogConfig] = useState(null);
    const [checkLoading, setCheckLoading] = useState(false);

    useEffect(() => {
        const currentPath = location.pathname;

        // 1. Check Global/Configurable Restrictions (High Priority)
        // Sort restrictions by priority (highest first)
        const sortedRestrictions = [...restrictions].sort((a, b) => (b.priority || 0) - (a.priority || 0));

        for (const restriction of sortedRestrictions) {
            const { flag, allowedPath } = restriction;
            let isRestricted = localStorage.getItem(flag) === 'true';

            // Check Redux auth state for specific flags if not present in localStorage
            if (flag === 'rekycRequired' && rekycRequired) {
                isRestricted = true;
            }

            if (isRestricted) {
                // If this restriction is active and user is not on the allowed path, redirect
                if (currentPath !== allowedPath) {
                    navigate(allowedPath, { replace: true });
                    return; // Stop checking other restrictions once we redirect
                }

                // If restricted but on allowed path, check if we need to show a dialog
                if (restriction.dialog) {
                    setDialogConfig(restriction.dialog);
                    return;
                }
            }
        }

        // 2. Check Global Service-Level Block List (from Login Response)
        if (blockedServices && blockedServices.length > 0) {
            const svc = identifyServiceByRoute(currentPath);
            if (svc) {
                const blockedSet = new Set(blockedServices.map((s) => String(s).toUpperCase()));
                if (blockedSet.has(String(svc).toUpperCase())) {
                    // Service is EXPLICITLY blocked!
                    navigate('/restricted', { replace: true });
                    return;
                }
            }
        }

        setDialogConfig(null);
    }, [location.pathname, navigate, restrictions, rekycRequired, blockedServices]);

    const handleDialogAction = async () => {
        if (dialogConfig?.onAgreeAction === 'CHECK_KYC_STATUS') {
            setCheckLoading(true);
            try {
                const response = await checkPermissionGet();
                if (response?.data?.status) {
                    const pixlKycStatus = response?.data?.pixlKycStatus;
                    const pixlKycVerificationStatus = response?.data?.pixlKycVerificationStatus;

                    localStorage.setItem('pixlKycStatus', pixlKycStatus || '');
                    localStorage.setItem('pixlKycVerificationStatus', pixlKycVerificationStatus || '');

                    if (pixlKycStatus === 'Verified' && pixlKycVerificationStatus === 'pending') {
                        // Still pending, maybe show toast
                        globalNotifySuccess('Status is still pending verification.');
                    } else {
                        // Status changed!
                        localStorage.setItem('kycPending', 'false');

                        // Re-evaluate restrictions
                        const anyEmpty = !pixlKycStatus || !pixlKycVerificationStatus;
                        const anyRejected = pixlKycStatus === 'Rejected' || pixlKycVerificationStatus === 'Rejected';
                        const kycRestricted = anyEmpty || anyRejected;
                        localStorage.setItem('kycRestricted', kycRestricted.toString());

                        globalNotifySuccess('Verification status updated!');
                        setDialogConfig(null);
                        window.location.reload();
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setCheckLoading(false);
            }
        }
    };

    return (
        <>
            {children}
            {dialogConfig && (
                <MessageDialog
                    open={true}
                    title={dialogConfig.title}
                    message={dialogConfig.message}
                    disableBackdropClick={dialogConfig.unclosable}
                    showCloseButton={!dialogConfig.unclosable}
                    onClose={() => setDialogConfig(null)}
                    agreeText={dialogConfig.agreeText}
                    onAgree={handleDialogAction}
                    agreeLoading={checkLoading}
                    showActions={!!dialogConfig.agreeText}
                />
            )}
        </>
    );
};

RouteGuard.propTypes = {
    children: PropTypes.node,
    restrictions: PropTypes.arrayOf(
        PropTypes.shape({
            flag: PropTypes.string.isRequired,
            allowedPath: PropTypes.string.isRequired,
            priority: PropTypes.number,
            dialog: PropTypes.shape({
                title: PropTypes.string,
                message: PropTypes.string,
                unclosable: PropTypes.bool
            })
        })
    )
};

export default RouteGuard;
